import * as turf from "@turf/turf";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  Geocoder,
  InfoToast,
  Seperator,
  useModals,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import {
  fetchAndProcessMapboundsFromGeocoder,
  useAuthContext,
} from "senf-shared";
import Dropdown from "../../../../core/dropdown/Dropdown";
import CesiumControls from "./CesiumControls";
import { GeoJSONUploader } from "./GeoJSONUploader";

const PlanningareaBuilder = ({
  formik,
  setDrawMode,
  drawMode,
  featureId,
  statefulMap,
  project,
  mapStyles,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { user } = useAuthContext();

  const handleMergeFeatures = (data) => {
    if (data?.features) {
      // Filter valid polygons
      const validFeatures = data.features.filter(
        (feature) =>
          feature.geometry &&
          (feature.geometry.type === "Polygon" ||
            feature.geometry.type === "MultiPolygon"),
      );

      if (validFeatures.length === 0) {
        return;
      }

      // If there's only one feature, use it directly
      if (validFeatures.length === 1) {
        formik.setFieldValue("mapbounds", {
          type: "FeatureCollection",
          features: validFeatures,
        });
        return;
      }

      // For multiple features, merge them using union
      const merged = validFeatures.reduce((acc, feature) => {
        if (!acc) return feature;
        try {
          return turf.union(acc, feature);
        } catch (error) {
          console.error("Error merging features:", error);
          return acc;
        }
      }, null);

      const mapbounds = merged
        ? {
            type: "FeatureCollection",
            features: [merged],
          }
        : {
            type: "FeatureCollection",
            features: [],
          };

      formik.setFieldValue("mapbounds", mapbounds);
    }
  };

  const handleSearch = async (result) => {
    if (!result) return;

    // Use the unified fetch and process function that handles Geocoder results
    await fetchAndProcessMapboundsFromGeocoder(
      result,
      // onResolve: Successfully fetched and processed boundaries
      (geoJson) => {
        // Merge with existing mapbounds if they exist
        if (formik.values.mapbounds) {
          geoJson.features = [
            ...geoJson.features,
            ...formik.values.mapbounds.features,
          ];
        }

        // Set the value directly
        formik.setFieldValue("mapbounds", geoJson);
      },
      // onError: Failed to fetch boundaries
      (error) => {
        console.error("Error fetching boundaries:", error);

        // Show error modal
        openModal(null, {
          title: t("map:builder.planningArea.noPlanningAreaFound.title"),
          description: t(
            "map:builder.planningArea.noPlanningAreaFound.description",
          ),
          zIndex: 999999999999999,
          id: "noPlanningAreaFoundModal",
        });
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col justify-center gap-4">
        <Geocoder
          callback={handleSearch}
          placeholder={t("util:search", { val: t("global:planningAreaShort") })}
          geolocate={false}
          types="region,postcode,district,place,locality,neighborhood"
        />

        <div className="flex gap-2 w-full">
          <div className="flex-1">
            <GeoJSONUploader
              text={t("util:upload", {
                val: t("global:planningAreaShort"),
              })}
              fileTypes={[".geojson"]}
              fileTypesText="GeoJSON"
              isButton={true}
              size="lg"
              width="max"
              className="w-full"
              classNameWrapper="w-full"
              onFileUpload={({ data }) => {
                // Check if data and data.features are defined to avoid errors
                if (isValidGeoJSON(JSON.stringify(data))) {
                  if (formik.values.mapbounds) {
                    data.features = [
                      ...data.features,
                      ...formik.values.mapbounds.features,
                    ];

                    handleMergeFeatures(data);
                  } else {
                    handleMergeFeatures(data);
                  }
                } else {
                  alert(t("global:invalidGeoJson"));
                }
              }}
            />
          </div>
          <div className="flex-1">
            <Button
              disabled={drawMode.drawType === "mapbounds" || featureId}
              text={t("util:draw", { val: t("global:planningAreaShort") })}
              width="max"
              size="lg"
              leadingIcon="edit"
              classNameWrapper="w-full"
              onClick={() => {
                setDrawMode({ mode: "draw", drawType: "mapbounds" });
              }}
            />
          </div>
        </div>

        {/* {user?.isSuperAdmin && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-4">
              <input type="text" placeholder="search" />
              <Button text="search"  onClick={() => {
                handleSearch({text: "Berlin"})
              }}/>

             

              {foundGeoJsons.length > 0 && (
                <div className="mt-4 flex flex-col gap-2">
                  <p className="body-md font-semibold">Found Boundaries:</p>
                  {foundGeoJsons.map((group) => (
                    <div key={`${group.adminLevel}-${group.name}`} className="flex flex-col gap-1 border border-gray-200 rounded p-2">
                      <div className="flex justify-between items-center">
                        <p className="body-sm">
                          {group.name} (Level {group.adminLevel})
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          text="Add"
                          onClick={() => handleAddBoundary(group.features)}
                        />
                      </div>
                      <p className="text-sm text-gray-500">
                        {group.features.length} boundaries found
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}  */}
      </div>
    </div>
  );
};

export default PlanningareaBuilder;
